import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { Typography } from '@beeline/design-system-react/extracted-styles';
import { getAuth } from '@beeline/lk-utils';

import { AxiosError } from 'axios';

import classes from './Widget.module.scss';
import { BookingInfo, findBookings } from './api/bookingsView';
import { SortDirection } from './api/types';
import { RoutePath } from './routes/types';
import { PlaceType } from './types';
import { toAddress } from './utils/toAddress';

const auth = getAuth();
const employeeNumber = auth?.getClaims()?.['Employee-Number'];

const emptyBookings: BookingInfo[] = [];

interface BookingProps {
  booking: BookingInfo;
}

const Booking: FC<BookingProps> = ({ booking }) => {
  const title = toAddress({
    town: booking.town.townName,
    building: booking.building.address,
    short: true,
  });

  const link = useMemo(() => {
    const path =
      booking.placeType === PlaceType.Desk
        ? RoutePath.DeskBooking
        : RoutePath.RoomBooking;
    const searchParams = new URLSearchParams({
      searched: JSON.stringify(booking),
    });
    return `/bookworkplace/${path}?${searchParams.toString()}`;
  }, [booking]);

  return (
    <a href={link} className={classes.link}>
      <Typography variant='body3' ellipsis>
        {title}
      </Typography>
    </a>
  );
};

interface BookingsProps {
  bookings: BookingInfo[];
}

const Bookings: FC<BookingsProps> = ({ bookings }) => {
  return (
    <div className={classes.bookingsContainer}>
      <Typography variant='body2'>Ваши последние бронирования</Typography>
      <div className={classes.bookingsList}>
        {bookings.map(booking => (
          <Booking key={booking.bookingId} booking={booking} />
        ))}
      </div>
    </div>
  );
};

export const Widget: FC = () => {
  const [bookings, setBookings] = useState(emptyBookings);
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);

  const getBookings = useCallback(async () => {
    if (!employeeNumber) return;

    try {
      setError(null);

      const response = await findBookings({
        filterEmployeeNumber: employeeNumber,
        bookedToAfter: new Date().toISOString(),
        sortDirection: SortDirection.Asc,
        sortField: 'dateBookingFrom',
        offset: 0,
        limit: 2,
      });

      setBookings(response.bookings);
    } catch (e) {
      setError((e as AxiosError).message);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    getBookings();
  }, [getBookings]);

  const content = useMemo(() => {
    if (loaded) {
      if (error) {
        return (
          <Typography variant='body3' className={classes.noResults}>
            {error}
          </Typography>
        );
      }
      if (!bookings.length) {
        return (
          <Typography variant='body3' className={classes.noResults}>
            Похоже у вас еще нет броней
          </Typography>
        );
      }
      return <Bookings bookings={bookings} />;
    }

    return null;
  }, [bookings, error, loaded]);

  return <div className={classes.container}>{content}</div>;
};
